import { ReactNode } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Button, SelectField } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import { countriesOptions, relationshipOptions } from '@utils/dictionary';

import { yupResolver } from '@hookform/resolvers/yup';
import { requiredSelectV2 } from '@shared/validations';

interface GuestLoginFormProps {
    bottomElement?: ReactNode;
    loading: boolean;
    openGuestModal: (data: GuestLoginFormValues) => void;
}

export const GuestLoginForm = ({
    bottomElement,
    loading,
    openGuestModal,
}: GuestLoginFormProps) => {
    const { t } = useTranslation();
    const {
        control,
        formState: { errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
    } = useForm<GuestLoginFormValues>({
        mode: 'onChange',
        resolver: yupResolver(GuestLoginFormSchema),
    });

    const { field: relationshipSelect } = useController({
        control,
        name: 'relationship',
    });

    const { field: countrySelect } = useController({
        control,
        name: 'country',
    });

    const isError = !!Object.keys(errors).length;

    return (
        <StandardForm>
            <SelectField
                {...relationshipSelect}
                error={!!errors.relationship}
                label={t('RELATIONSHIP_TO_COMPANY')}
                maxMenuHeight={200}
                message={errors.relationship?.message}
                options={relationshipOptions()}
                placeholder={t('RELATIONSHIP_TO_COMPANY_PLACEHOLDER')}
                testId="select_guest-login-relationship"
            />
            <SelectField
                {...countrySelect}
                error={!!errors.country}
                label={t('COUNTRY')}
                maxMenuHeight={150}
                message={errors.country?.message}
                options={countriesOptions()}
                placeholder={t('SELECT_COUNTRY')}
                testId="select_guest-login-country"
            />
            <StandardForm.Actions>
                <Button
                    disabled={
                        loading ||
                        isSubmitting ||
                        (!isValid && isSubmitted) ||
                        isError
                    }
                    fullWidth
                    isLoading={loading || isSubmitting}
                    onClick={handleSubmit(openGuestModal)}
                    testId="button_continue-as-guest"
                    type={'submit'}
                    variant={'primary'}
                >
                    {t('CONTINUE_AS_GUEST')}
                </Button>
                {bottomElement && bottomElement}
            </StandardForm.Actions>
        </StandardForm>
    );
};

const GuestLoginFormSchema = yup.object({
    country: requiredSelectV2,
    relationship: requiredSelectV2,
});

export type GuestLoginFormValues = yup.InferType<typeof GuestLoginFormSchema>;
